// Sign-in buttons. Discord + Spotify OAuth (both configured in Supabase), plus
// guest mode (Supabase anonymous sign-in) — no account, hidden public page by
// default, claimable later from the dashboard. Kept in one place so the
// landing and invite pages stay consistent.

import { useState } from "react";
import { useAuth } from "../auth/AuthProvider.tsx";
import { friendlyAuthError } from "../lib/anonMode.ts";
import { track, clientHints } from "../lib/analytics.ts";

export function AuthButtons({
  discordLabel = "Continue with Discord",
}: {
  discordLabel?: string;
}) {
  const auth = useAuth();
  const [guestBusy, setGuestBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = (provider: "discord" | "spotify") => {
    // Log the funnel entry BEFORE redirecting away (the redirect leaves the SPA).
    track("auth_start", { provider, ...clientHints() });
    auth
      .signInWithOAuth(provider)
      .catch((e) =>
        track("auth_start_error", { provider, error: String(e?.message ?? e) }),
      );
  };
  const startGuest = async () => {
    track("auth_start", { provider: "anonymous", ...clientHints() });
    setGuestBusy(true);
    setError(null);
    try {
      // No redirect: the session appears in place and the landing/invite
      // effects route onward (dashboard bounces to onboarding sans profile).
      await auth.signInAnonymously();
    } catch (e) {
      track("auth_start_error", {
        provider: "anonymous",
        error: String((e as Error)?.message ?? e),
      });
      setError(friendlyAuthError(e));
      setGuestBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        data-testid="action-signin-discord"
        className="btn-discord"
        onClick={() => start("discord")}
      >
        <DiscordMark /> {discordLabel}
      </button>
      <button
        type="button"
        data-testid="action-signin-spotify"
        className="inline-flex min-h-[44px] select-none items-center justify-center gap-2 rounded-lg bg-[#1DB954] px-4 py-2.5 font-medium text-black transition-colors hover:bg-[#1ed760]"
        onClick={() => start("spotify")}
      >
        <SpotifyMark /> Continue with Spotify
      </button>
      <button
        type="button"
        data-testid="action-signin-guest"
        className="inline-flex min-h-[44px] select-none items-center justify-center gap-2 rounded-lg border border-border-strong bg-card px-4 py-2.5 font-medium text-ink-900 transition-colors hover:bg-paper-100 disabled:opacity-60"
        disabled={guestBusy}
        onClick={startGuest}
      >
        {guestBusy ? "Setting up…" : "Try it without an account"}
      </button>
      <p className="text-xs text-ink-400">
        Guest profiles stay off the public pages and live only in this browser —
        you can claim one with Discord or Spotify later.
      </p>
      {error && (
        <p data-testid="guest-signin-error" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

const DiscordMark = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M20.3 4.4A19.8 19.8 0 0015.4 3l-.2.5c1.7.4 2.9 1 4 1.7a13.6 13.6 0 00-10.3 0c1-.7 2.3-1.3 4-1.7L12.6 3a19.8 19.8 0 00-4.9 1.4C4.3 9.3 3.5 14 3.9 18.7a19.9 19.9 0 006 3l.7-1.2a13 13 0 01-2-1l.5-.4a14.2 14.2 0 0012 0l.5.4c-.6.4-1.3.7-2 1l.7 1.2a19.9 19.9 0 006-3c.5-5.5-.8-10.2-3.7-14.3zM9.5 15.4c-1.2 0-2.1-1.1-2.1-2.4S8.3 10.6 9.5 10.6s2.1 1.1 2.1 2.4-.9 2.4-2.1 2.4zm5 0c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.1 1.1 2.1 2.4-.9 2.4-2.1 2.4z" />
  </svg>
);

const SpotifyMark = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm4.6 14.4a.62.62 0 01-.86.21c-2.35-1.44-5.3-1.76-8.79-.96a.62.62 0 11-.28-1.22c3.81-.87 7.08-.5 9.72 1.11.3.18.39.57.21.86zm1.23-2.73a.78.78 0 01-1.07.26c-2.69-1.65-6.79-2.13-9.97-1.17a.78.78 0 11-.45-1.5c3.63-1.1 8.15-.56 11.24 1.34.37.22.48.7.25 1.07zm.1-2.85C14.8 8.98 9.5 8.8 6.4 9.74a.94.94 0 11-.54-1.8c3.56-1.08 9.4-.87 13.11 1.33a.94.94 0 01-.96 1.62z" />
  </svg>
);
